import {useCallback, useEffect, useState} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import {useDispatch, useSelector} from 'react-redux';
import {navigate, getCurrentRoute} from './navigationService';
import {
    clearState,
    checkCurrentSessionTokenValid,
    loginUser,
    createApiAuthToken,
    fetchAllCurrentActivities,
} from '../reducers/cbcAuthorizationSlice';
import {CommonActions} from '@react-navigation/native';
import {Platform} from 'react-native';
import {checkReminderMessages} from "../reducers/cbcReminderMessagesSlice";
import useLocalNotification from "./localNotificationHandler";
import useLocalNotificationJSONManager from "./useLocalNotificationJSONManager";
import checkReminderMessagesFunction from "./checkReminderMessages";

export const useQuickAuthentication = (hideSpinner) => {
    const {
        scheduleNotification,
        scheduleRecurringNotification,
        getAllScheduledNotifications,
        cancelNotification
    } = useLocalNotification();
    const {
        notificationGroups,
        createNotificationGroup,
        updateNotificationGroup,
        deleteNotificationGroup,
        loadNotificationGroups,
        saveNotificationGroups,
        createNotificationGroupFromDetails
    } = useLocalNotificationJSONManager();

    const currentRoute = getCurrentRoute();
    const dispatch = useDispatch();
    const {
        authStatus,
        authStepStatus,
        wpAuthorizationToken,
        apiAuthToken,
        userId,
    } = useSelector((state) => state.cbcAuthorization);

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [attemptedReLogin, setAttemptedReLogin] = useState(false);
    const [authenticateCalled, setAuthenticateCalled] = useState(false);

    const authenticate = useCallback(async () => {
        if (authenticateCalled === false) {
            setAuthenticateCalled(true);
            console.log('Authenticating');
            const currentUserId = await AsyncStorage.getItem('userId');
            console.log('Stored User ID', currentUserId);
            const sessionToken = await SecureStore.getItemAsync('apiAuthToken');
            const jwtAuthToken = await SecureStore.getItemAsync('authToken');

            if (!currentUserId || !sessionToken || !jwtAuthToken) {
                console.log('No valid session, login required');
                dispatch(clearState());
                hideSpinner();
                navigate('login');
                //logUserIn();
                setIsAuthenticated(false);
            } else {
                console.log('Dispatching check current session', currentUserId);
                dispatch(
                    checkCurrentSessionTokenValid({
                        sessionToken: sessionToken,
                        jwtAuthToken,
                        userId: currentUserId
                    })
                );
            }
        }
    }, [dispatch]);

    useEffect(() => {
        if (authenticateCalled === true) {
            const reLogin = async () => {
                console.log('Auth Status to Re-Login', authStatus);
                console.log('Auth Step Status to Re-Login', authStepStatus);
                const currentUserId = await AsyncStorage.getItem('userId');
                console.log('Stored User ID', currentUserId);
                if (authStatus === 'failed' && authStepStatus === 'failed') {
                    // user credentials have most likely changed, set authenticated to
                    // false and send the user to the login screen
                    console.log('dispatch(clearState()); CALLED IN QUICK AUTH');
                    await AsyncStorage.setItem('userId', '0');
                    dispatch(clearState());
                    console.log('hideSpinner if (authStatus === \'failed\' && authStepStatus === \'failed\')');
                    setAuthenticateCalled(false)
                    logUserIn();
                } else if (authStatus === 'failed') {
                    console.log('Logging User Back In');
                    console.log('hideSpinner else if (authStatus === \'failed\') ');
                    hideSpinner();
                    logUserIn();
                } else if (authStatus === 'success') {
                    console.log('hideSpinner else if (authStatus === \'success\')');
                    hideSpinner();
                    setAuthenticateCalled(false)
                    setIsAuthenticated(true);
                }
            };
            reLogin();
        }
    }, [authStatus, authStepStatus]);

    useEffect(() => {
        if (authenticateCalled === true) {
            console.log('Auth Status to set authenticated', authStatus);
            setIsAuthenticated(authStatus === 'success');
            console.log('hideSpinner authenticateCalled === true');
            //hideSpinner();
        }
    }, [authStatus]);

    // New useEffect for processing the login once wpAuthorizationToken is available
    useEffect(() => {
        if (authenticateCalled === true) {
            const processLogin = async () => {
                if (wpAuthorizationToken && userId) {
                    // Check if userId is truthy
                    console.log(
                        'Processing login with wpAuthorizationToken:',
                        wpAuthorizationToken
                    );
                    await AsyncStorage.setItem('userId', userId.toString());
                    await SecureStore.setItemAsync('authToken', wpAuthorizationToken);

                    dispatch(
                        createApiAuthToken({
                            userId,
                            jwtAuthToken: wpAuthorizationToken,
                        })
                    );
                }
            };
            if (
                !['login', 'loadScreen', null].includes(currentRoute) &&
                Platform.OS !== 'web'
            ) {
                processLogin();
            }
        }
    }, [wpAuthorizationToken, userId, dispatch]);

    useEffect(() => {
        if (authenticateCalled === true) {
            const processNewApiToken = async () => {
                if (apiAuthToken) {
                    console.log('Processing new API token:', apiAuthToken);
                    await SecureStore.setItemAsync('apiAuthToken', apiAuthToken);
                    dispatch(checkReminderMessages(userId)).then((e) => {
                        console.log('checkReminderMessages quickAuth', e);
                        checkReminderMessagesFunction(
                            scheduleNotification,
                            scheduleRecurringNotification,
                            cancelNotification,
                            notificationGroups,
                            {
                                createNotificationGroup,
                                updateNotificationGroup,
                                deleteNotificationGroup,
                                loadNotificationGroups,
                                saveNotificationGroups,
                                createNotificationGroupFromDetails
                            }
                        );
                    });
                }
            };

            if (
                apiAuthToken &&
                !['login', 'loadScreen', null].includes(currentRoute) &&
                Platform.OS !== 'web'
            ) {
                processNewApiToken();
            }
        }
    }, [apiAuthToken, dispatch]);

    const logUserIn = async () => {
        console.log('logUserIn called');
        if (authenticateCalled === true) {
            console.log('Attempting to log user in with saved credentials');
            const username = await SecureStore.getItemAsync('username');
            const password = await SecureStore.getItemAsync('password');
            if (username && password) {
                dispatch(loginUser({username, password})).then(() => {
                    if(userId !== 0 || userId !== null) {
                        dispatch(checkReminderMessages(userId)).then((e) => {
                            console.log('checkReminderMessages quickAuth', e);
                            checkReminderMessagesFunction(
                                scheduleNotification,
                                scheduleRecurringNotification,
                                cancelNotification,
                                notificationGroups,
                                {
                                    createNotificationGroup,
                                    updateNotificationGroup,
                                    deleteNotificationGroup,
                                    loadNotificationGroups,
                                    saveNotificationGroups,
                                    createNotificationGroupFromDetails
                                }
                            );
                        });
                    }
                });
            } else {
                console.log('No saved credentials found, login required');
                dispatch(clearState());
                navigate('login');
                console.log('hideSpinner \'No saved credentials found, login required\'');
                hideSpinner();
                setAuthenticateCalled(false)
                setIsAuthenticated(false);
            }
        }
    };

    return {isAuthenticated, authenticate};
};
